// @ts-nocheck

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
} from 'recharts';
import {
  Box,
  Typography,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Paper,
  Stack,
} from '@mui/material';
import { AppDispatch } from '@/store';
import { getCompanyData } from '@/store/data/thunks';
import {
  selectCompanyData,
  selectCompanyDataLoading,
  selectCompanyDataError,
} from '@/store/data/selectors';
import { GetCompanyDataRequest, CompanyDataSchema } from '@/types/data';
import dataVisualStyles from './dataVisual.module.css';

// Chart type options
type ChartType = 'line' | 'bar' | 'area' | 'pie' | 'scatter';

interface DataVisualProps {
  // Optional props for when used as child component
  title?: string;
  height?: number;
  showControls?: boolean;
  defaultFilters?: Partial<GetCompanyDataRequest>;
  className?: string;
}

interface ChartData {
  name: string;
  value: number;
  [key: string]: any;
}

const COLORS = [
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7c7c',
  '#8dd1e1',
  '#d084d0',
  '#87d068',
  '#ffa940',
  '#597ef7',
  '#ff9c6e',
];

const DataVisual: React.FC<DataVisualProps> = ({
  title = 'Data Visualization Dashboard',
  height = 400,
  showControls = true,
  defaultFilters = {},
  className = '',
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();

  // Redux state
  const companyData = useSelector(selectCompanyData);
  const isLoading = useSelector(selectCompanyDataLoading);
  const error = useSelector(selectCompanyDataError);

  // Local state for controls
  const [chartType, setChartType] = useState<ChartType>('line');
  const [filters, setFilters] = useState<GetCompanyDataRequest>({
    limit: 100,
    offset: 0,
    ...defaultFilters,
  });
  const [includeFields, setIncludeFields] = useState<string[]>([]);
  // Add this with your other state variables
  const [groupBy, setGroupBy] = useState<string>('');
  // Extract URL parameters and apply to filters
  useEffect(() => {
    const urlStudyIds = searchParams.get('study_ids');

    if (urlStudyIds) {
      // Handle single study ID or comma-separated study IDs
      const studyIdsArray = urlStudyIds
        .split(',')
        .map(id => id.trim())
        .filter(id => id);

      setFilters(prev => ({
        ...prev,
        study_id__in: studyIdsArray,
      }));

      // Optionally set group by to study_name when filtering by specific studies
      setGroupBy('study_name');
    }
  }, [searchParams]);

  // Handle include fields changes
  useEffect(() => {
    if (includeFields.length > 0) {
      setFilters(prev => ({
        ...prev,
        include: includeFields,
      }));
    } else {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.include;
        return newFilters;
      });
    }
  }, [includeFields]);

  // Load data on mount and filter changes
  useEffect(() => {
    dispatch(getCompanyData(filters));
  }, [dispatch, filters]);

  // Enhanced filter handler that triggers API calls
  const handleFilterChange = (key: keyof GetCompanyDataRequest, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  // Handle include field checkbox changes
  const handleIncludeFieldChange = (field: string, checked: boolean) => {
    if (checked) {
      setIncludeFields(prev => [...prev, field]);
    } else {
      setIncludeFields(prev => prev.filter(f => f !== field));
    }
  };

  // Clear URL filters (optional functionality)
  const clearUrlFilters = () => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters.study_id__in;
      return newFilters;
    });
    // Optionally clear URL params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('study_ids');
    window.history.replaceState({}, '', `${window.location.pathname}?${newSearchParams}`);
  };

  // Render chart based on type
  const renderChart = () => {
    if (!companyData.length) {
      return <div className={dataVisualStyles.noData}>No data available for current filters</div>;
    }

    const commonProps = {
      data: companyData,
      margin: { top: 20, right: 30, left: 20, bottom: 5 },
    };

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="company_name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke={COLORS[0]}
              strokeWidth={3}
              name="Revenue"
            />
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="company_name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area
              type="monotone"
              dataKey="revenue"
              stackId="1"
              stroke={COLORS[0]}
              fill={COLORS[0]}
              fillOpacity={0.6}
            />
          </AreaChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="company_name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="revenue" fill={COLORS[0]} />
          </BarChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={companyData.slice(0, 8)} // Limit for readability
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={120}
              fill="#8884d8"
              dataKey="revenue"
            >
              {companyData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        );

      case 'scatter':
        return (
          <ScatterChart {...commonProps}>
            <CartesianGrid />
            <XAxis dataKey={primaryMetric} name={primaryMetric} />
            <YAxis dataKey={secondaryMetric} name={secondaryMetric} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Scatter name="Companies" data={chartData} fill={COLORS[0]} />
          </ScatterChart>
        );

      default:
        return null;
    }
  };

  if (error) {
    return (
      <div className={`${dataVisualStyles.dataVisual} ${dataVisualStyles.error} ${className}`}>
        <div className={dataVisualStyles.errorContent}>
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button onClick={() => dispatch(getCompanyData(filters))}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${dataVisualStyles.dataVisual} ${className}`}>
      <div className={dataVisualStyles.mainLayout}>
        {/* Left Content Area */}
        <div className={dataVisualStyles.contentArea}>
          {title && <h2 className={dataVisualStyles.title}>{title}</h2>}

          {/* Active Filters Display */}
          {filters.study_id__in && filters.study_id__in.length > 0 && (
            <div className={dataVisualStyles.activeFilters}>
              <span className={dataVisualStyles.filterLabel}>
                Filtered by Studies: {filters.study_id__in.join(', ')}
              </span>
              <button
                className={dataVisualStyles.clearFilter}
                onClick={clearUrlFilters}
                title="Clear study filter"
              >
                ×
              </button>
            </div>
          )}

          {/* Chart */}
          <div className={dataVisualStyles.chartContainer}>
            {isLoading ? (
              <div className={dataVisualStyles.loading}>
                <div className={dataVisualStyles.spinner}></div>
                <p>Loading data...</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={height}>
                {renderChart()}
              </ResponsiveContainer>
            )}
          </div>

          {/* Data Table (optional) */}
          {companyData.length > 0 && (
            <div className={dataVisualStyles.tableContainer}>
              <h3>Data Summary</h3>
              <table className={dataVisualStyles.table}>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Revenue</th>
                    <th>Market Share</th>
                  </tr>
                </thead>
                <tbody>
                  {companyData.slice(0, 10).map((item, index) => (
                    <tr key={index}>
                      <td>{item.company_name}</td>
                      <td>
                        {typeof item.revenue === 'number'
                          ? item.revenue.toLocaleString()
                          : item.revenue}
                      </td>
                      <td>
                        {typeof item.market_share === 'number'
                          ? item.market_share.toLocaleString()
                          : item.market_share}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right Filter Panel - Always Visible */}
        <div className={dataVisualStyles.filterPanel}>
          <div className={dataVisualStyles.filterPanelContent}>
            {/* Header */}
            <div className={dataVisualStyles.filterHeader}>
              <Typography variant="h6" className={dataVisualStyles.filterTitle}>
                Filter Options
              </Typography>
            </div>

            {/* Chart Controls Section */}
            <div className={dataVisualStyles.filterSection}>
              <Typography variant="subtitle1" className={dataVisualStyles.sectionTitle}>
                Chart Options
              </Typography>

              <Paper className={dataVisualStyles.filterCard}>
                <div className={dataVisualStyles.chartControls}>
                  <div className={dataVisualStyles.controlField}>
                    <label>Chart Type:</label>
                    <select
                      value={chartType}
                      onChange={e => setChartType(e.target.value as ChartType)}
                      className={dataVisualStyles.selectField}
                    >
                      <option value="line">Line Chart</option>
                      <option value="bar">Bar Chart</option>
                      <option value="area">Area Chart</option>
                      <option value="pie">Pie Chart</option>
                      <option value="scatter">Scatter Plot</option>
                    </select>
                  </div>
                </div>
              </Paper>
            </div>

            {/* Include Fields Section */}
            <div className={dataVisualStyles.filterSection}>
              <Typography variant="subtitle1" className={dataVisualStyles.sectionTitle}>
                Include Fields
              </Typography>

              <Paper className={dataVisualStyles.filterCard}>
                <FormGroup className={dataVisualStyles.checkboxGroup}>
                  {[
                    { value: 'revenue', label: 'Revenue' },
                    { value: 'units_sold', label: 'Units Sold' },
                    { value: 'yoy_revenue', label: 'Year over Year Revenue' },
                    { value: 'yoy_revenue_percentage', label: 'YoY Revenue Percentage' },
                    { value: 'yoy_units_sold', label: 'YoY Units Sold' },
                    { value: 'yoy_units_sold_percentage', label: 'YoY Units Sold Percentage' },
                    { value: 'selling_price', label: 'Selling Price' },
                    { value: 'yoy_selling_price', label: 'YoY Selling Price' },
                    { value: 'number_of_companies', label: 'Number of Companies' },
                    { value: 'number_of_segments', label: 'Number of Segments' },
                    { value: 'avg_company_revenue', label: 'Average Company Revenue' },
                    { value: 'py_revenue', label: 'Previous Year Revenue' },
                    { value: 'number_of_years_available', label: 'Number of Years Available' },
                  ].map(field => (
                    <FormControlLabel
                      key={field.value}
                      control={
                        <Checkbox
                          checked={includeFields.includes(field.value)}
                          onChange={e => handleIncludeFieldChange(field.value, e.target.checked)}
                          size="small"
                          className={dataVisualStyles.checkbox}
                        />
                      }
                      label={
                        <Typography variant="body2" className={dataVisualStyles.checkboxLabel}>
                          {field.label}
                        </Typography>
                      }
                      className={dataVisualStyles.checkboxItem}
                    />
                  ))}
                </FormGroup>

                {/* Selected Fields Summary */}
                {includeFields.length > 0 && (
                  <div className={dataVisualStyles.selectedFields}>
                    <Typography variant="caption" className={dataVisualStyles.selectedCount}>
                      Selected ({includeFields.length})
                    </Typography>
                    <div className={dataVisualStyles.chipContainer}>
                      {includeFields.map(field => (
                        <div key={field} className={dataVisualStyles.chip}>
                          {field.replace(/_/g, ' ')}
                          <button
                            className={dataVisualStyles.chipRemove}
                            onClick={() => handleIncludeFieldChange(field, false)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Paper>
            </div>

            {/* Year Filter Section */}
            <div className={dataVisualStyles.filterSection}>
              <Typography variant="subtitle1" className={dataVisualStyles.sectionTitle}>
                Year Filter
              </Typography>

              <Paper className={dataVisualStyles.filterCard}>
                <TextField
                  type="number"
                  label="Filter by Year"
                  placeholder="e.g., 2024"
                  value={filters.year__eq || ''}
                  onChange={e =>
                    handleFilterChange('year__eq', e.target.value ? parseInt(e.target.value) : null)
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={dataVisualStyles.textField}
                />

                {filters.year__eq && (
                  <div className={dataVisualStyles.activeFilter}>
                    <Typography variant="caption" className={dataVisualStyles.activeFilterText}>
                      Active filter: Year {filters.year__eq}
                    </Typography>
                  </div>
                )}
              </Paper>
            </div>

            {/* Action Buttons */}
            <div className={dataVisualStyles.filterActions}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setFilters({ limit: 100, offset: 0 });
                  setIncludeFields([]);
                  setChartType('line');
                }}
                className={dataVisualStyles.clearButton}
              >
                Clear All
              </Button>

              <Button variant="contained" fullWidth className={dataVisualStyles.applyButton}>
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataVisual;
